import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { statsService } from "../Services/statisticsService";
import { StatsImporter } from "../Components/ImportJsonStats";

// Stat Card Component
const StatCard = ({ title, value, icon, trend, color = "red" }) => (
  <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <span className="text-3xl">{icon}</span>
      {trend && (
        <span
          className={`text-sm font-semibold ${
            trend > 0 ? "text-green-600" : "text-red-600"
          }`}
        >
          {trend > 0 ? "+" : ""}
          {trend}%
        </span>
      )}
    </div>
    <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
    <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
  </div>
);

// Loading Skeleton
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-gray-200 rounded-lg h-32"></div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="bg-gray-200 rounded-lg h-64"></div>
      ))}
    </div>
  </div>
);

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("week");
  const [showAdminView, setShowAdminView] = useState(false);

  useEffect(() => {
    loadStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await statsService.getStatistics(timeRange);
      if (data) {
        setStats(data);
      } else {
        setError("Failed to load statistics");
      }
    } catch (err) {
      console.error("Error loading stats:", err);
      setError("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!stats?.week?.dailyData || !Array.isArray(stats.week.dailyData))
      return [];
    return stats.week.dailyData
      .filter((day) => day && day.date) // Filter out invalid entries
      .slice(-7)
      .map((day) => ({
        date: new Date(day.date).toLocaleDateString("en", { weekday: "short" }),
        games: day.games || 0,
        players: day.players || 0,
        completed: day.completed || 0,
      }));
  };
  const prepareHourlyData = () => {
    if (!stats?.week?.popularHours) return [];
    return Object.entries(stats.week.popularHours)
      .map(([hour, data]) => ({
        hour: `${hour}:00`,
        games: data.games,
        players: data.players,
      }))
      .sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  };
  const prepareCategoryData = () => {
    if (!stats?.popularCategories) return [];

    // Normalize category names
    const normalizedCategories = {};

    Object.entries(stats.popularCategories).forEach(([category, count]) => {
      // Normalize the category name
      let normalized = category
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .replace(/boys?/g, "boy") // "boys" or "boy" becomes "boy"
        .replace(/girls?/g, "girl") // "girls" or "girl" becomes "girl"
        .replace(/names?\s+of\s+/g, "name of ") // "names of" or "name of" becomes "name of"
        .replace(/things?\s+/g, "thing ") // "things" becomes "thing"
        .replace(/animals?\s+/g, "animal ") // "animals" becomes "animal"
        .replace(/places?\s+/g, "place ") // "places" becomes "place"
        .replace(/foods?\s+/g, "food ") // "foods" becomes "food"
        .replace(/\s+in\s+.*$/g, "") // Remove " in [location]" suffixes
        .replace(/\s+that\s+.*$/g, "") // Remove " that [description]" suffixes
        .replace(/\s+with\s+.*$/g, "") // Remove " with [description]" suffixes
        .replace(/countries/g, "country"); // "countries" becomes "country"

      // Capitalize first letter of each word for display
      const displayName = normalized
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // Accumulate counts for normalized categories
      if (normalizedCategories[displayName]) {
        normalizedCategories[displayName] += count;
      } else {
        normalizedCategories[displayName] = count;
      }
    });

    return Object.entries(normalizedCategories)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8) // Show top 8 instead of 10 for better readability
      .map(([category, count]) => ({
        name: category,
        value: count,
      }));
  };

  const COLORS = [
    "#DC2626",
    "#F59E0B",
    "#10B981",
    "#3B82F6",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#F97316",
  ];

  if (loading && !stats) return <LoadingSkeleton />;
  if (error) return <div className="text-center text-red-600 p-8">{error}</div>;
  if (!stats) return null;

  const currentStats = stats[timeRange] || stats.week;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-orange-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-red-800 mb-2">
                NameIt! Statistics Dashboard
              </h1>
              <p className="text-gray-600">
                Real-time game analytics and player insights
              </p>
            </div>
            <div className="flex items-center gap-4 mt-4 md:mt-0">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
              {/* <button
                onClick={() => setShowAdminView(!showAdminView)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                {showAdminView ? "Public View" : "Admin View"}
              </button> */}
            </div>
          </div>

          {/* Live Indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Live data • Updates every 30 seconds</span>
          </div>
        </div>
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Games"
            value={formatNumber(currentStats.gamesCreated || 0)}
            icon="🎮"
            // trend={12}
          />
          <StatCard
            title="Active Players"
            value={formatNumber(currentStats.uniquePlayers || 0)}
            icon="👥"
            // trend={8}
          />
          <StatCard
            title="Completion Rate"
            value={`${currentStats.completionRate || 0}%`}
            icon="✅"
            color="green"
          />
          <StatCard
            title="Avg Game Time"
            value={formatDuration(currentStats.avgGameDuration || 0)}
            icon="⏱️"
            color="blue"
          />
        </div>
        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Activity Chart */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Daily Activity</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={prepareChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="games"
                  stroke="#DC2626"
                  name="Games"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="players"
                  stroke="#3B82F6"
                  name="Players"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Hourly Distribution */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Peak Hours</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={prepareHourlyData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="games" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Additional Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Game Types */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Game Types</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Default Categories</span>
                  <span className="font-semibold">
                    {currentStats.defaultCategoryGames || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-600 h-2 rounded-full"
                    style={{
                      width: `${
                        ((currentStats.defaultCategoryGames || 0) /
                          ((currentStats.defaultCategoryGames || 0) +
                            (currentStats.customCategoryGames || 0))) *
                          100 || 0
                      }%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">Custom Categories</span>
                  <span className="font-semibold">
                    {currentStats.customCategoryGames || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${
                        ((currentStats.customCategoryGames || 0) /
                          ((currentStats.defaultCategoryGames || 0) +
                            (currentStats.customCategoryGames || 0))) *
                          100 || 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Popular Custom Categories */}
          {/* Popular Custom Categories */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Popular Categories</h3>
            {prepareCategoryData().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={prepareCategoryData()}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={false} // Remove labels from pie slices
                    outerRadius={70} // Reduced size
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prepareCategoryData().map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} uses`, name]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={60}
                    wrapperStyle={{ fontSize: "12px" }}
                    formatter={(value, entry) =>
                      `${value} (${(
                        (entry.payload.value /
                          prepareCategoryData().reduce(
                            (sum, item) => sum + item.value,
                            0
                          )) *
                        100
                      ).toFixed(0)}%)`
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No custom categories used yet
              </p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Peak Concurrent Games</span>
                <span className="font-semibold">
                  {currentStats.peakConcurrentGames || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Peak Hour</span>
                <span className="font-semibold">
                  {currentStats.peakHour || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Abandoned Games</span>
                <span className="font-semibold text-red-600">
                  {currentStats.gamesAbandoned || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Current Active</span>
                <span className="font-semibold text-green-600">
                  {stats.today?.currentConcurrentGames || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Admin-only Section */}
        {showAdminView && (
          <div className="bg-gray-900 text-white rounded-lg shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4">🔒 Admin Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-gray-400 text-sm mb-2">Server Load</h4>
                <p className="text-2xl font-bold">
                  {stats.today?.currentConcurrentGames || 0} / 20
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${
                      (stats.today?.currentConcurrentGames || 0) > 15
                        ? "bg-red-500"
                        : "bg-green-500"
                    }`}
                    style={{
                      width: `${
                        ((stats.today?.currentConcurrentGames || 0) / 20) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-gray-400 text-sm mb-2">Database Usage</h4>
                <p className="text-2xl font-bold">
                  ~{formatNumber((currentStats.gamesCreated || 0) * 15)} ops
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Estimated Firebase operations
                </p>
              </div>
              <div>
                <h4 className="text-gray-400 text-sm mb-2">Cost Projection</h4>
                <p className="text-2xl font-bold">
                  ${((currentStats.gamesCreated || 0) * 0.0001).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Estimated daily cost
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Fun Facts */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold mb-4">  Fun Facts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total words submitted</p>
              <p className="text-2xl font-bold text-red-600">
                ~{formatNumber((currentStats.gamesCreated || 0) * 16)}
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Most active day</p>
              <p className="text-2xl font-bold text-blue-600">
                {currentStats.dailyData?.reduce(
                  (max, day) => (day.games > (max?.games || 0) ? day : max),
                  null
                )?.date
                  ? new Date(
                      currentStats.dailyData.reduce(
                        (max, day) =>
                          day.games > (max?.games || 0) ? day : max,
                        null
                      ).date
                    ).toLocaleDateString("en", { weekday: "long" })
                  : "N/A"}
              </p>
            </div>
            {/* <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Letters most played</p>
              <p className="text-2xl font-bold text-green-600">S, A, C</p>
            </div> */}
          </div>
        </div>

        {/* Export and Cleanup Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {showAdminView && (
            <>
              <button
                onClick={() => {
                  const dataStr = JSON.stringify(stats, null, 2);
                  const dataUri =
                    "data:application/json;charset=utf-8," +
                    encodeURIComponent(dataStr);
                  const exportFileDefaultName = `nameit-stats-${
                    new Date().toISOString().split("T")[0]
                  }.json`;
                  const linkElement = document.createElement("a");
                  linkElement.setAttribute("href", dataUri);
                  linkElement.setAttribute("download", exportFileDefaultName);
                  linkElement.click();
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                📊 Export Statistics
              </button>

              <button
                onClick={async () => {
                  if (
                    confirm(
                      "Fix concurrent game counts? This will correct any drift in active game tracking."
                    )
                  ) {
                    try {
                      const { fixConcurrentGameCounts, cleanupOldGameEvents } =
                        await import("../Services/statisticsService");

                      // Fix concurrent counts first
                      const fixResult =
                        await statsService.fixConcurrentGameCounts();

                      // Clean up old events
                      const cleanupResult =
                        await statsService.cleanupOldGameEvents();

                      if (fixResult.success && cleanupResult.success) {
                        alert(
                          `Maintenance completed!\n- Concurrent games corrected: ${fixResult.correctedCount}\n- Old games cleaned: ${cleanupResult.cleaned}`
                        );
                        loadStats(); // Refresh stats
                      } else {
                        alert(
                          `Maintenance had issues: ${
                            fixResult.error || cleanupResult.error
                          }`
                        );
                      }
                    } catch (error) {
                      alert(`Error: ${error.message}`);
                    }
                  }
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                🔧 Fix Concurrent Counts
              </button>

              <button
                onClick={async () => {
                  if (
                    confirm(
                      "Run statistics cleanup? This will archive old data and optimize storage."
                    )
                  ) {
                    try {
                      const { manualCleanup } = await import(
                        "../Services/statsCleanupJob"
                      );
                      const result = await manualCleanup();
                      if (result.success) {
                        alert("Cleanup completed successfully!");
                        loadStats(); // Refresh stats
                      } else {
                        alert(`Cleanup failed: ${result.error}`);
                      }
                    } catch (error) {
                      alert(`Error: ${error.message}`);
                    }
                  }
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                🧹 Run Cleanup
              </button>

              <button
                onClick={async () => {
                  const confirmMsg = `⚠️ MIGRATION WARNING ⚠️

This will:
- Import historical games into statistics
- MERGE with existing data (won't overwrite)
- Process all games in your database
- May take several minutes

The migration is now SAFE and won't destroy your current stats.

Continue?`;

                  if (confirm(confirmMsg)) {
                    try {
                      const { migrateExistingGames, checkMigrationStatus } =
                        await import("../Services/migrateExistingData");

                      alert(
                        "Starting migration... This may take a moment. Check console for progress."
                      );

                      const result = await migrateExistingGames();

                      if (result.success) {
                        alert(
                          `✅ Migration completed successfully!\n\n` +
                            `📊 Results:\n` +
                            `- Games migrated: ${result.totalMigrated}\n` +
                            `- Games skipped (already done): ${result.skipped}\n` +
                            `- Days processed: ${result.daysProcessed}\n` +
                            `- Errors: ${result.errors}\n\n` +
                            `Your existing stats have been preserved and enhanced with historical data.`
                        );
                        loadStats(); // Refresh stats
                      } else {
                        alert(`❌ Migration failed: ${result.error}`);
                      }
                    } catch (error) {
                      alert(`❌ Error: ${error.message}`);
                      console.error(error);
                    }
                  }
                }}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                📥 Safe Import Historical Data
              </button>
            </>
          )}
        </div>
        {/* <StatsImporter /> */}
      </div>
    </div>
  );
};

export default StatsPage;
